/*
 * The table composite's view — what a Member filtered, sorted and paged to — and its translation to
 * and from the URL's search parameters, which is what makes a view survive a reload and travel as a
 * link. `?role=admin&groups=g-1&groups=g-2&sort=-last-active&page=2` reads: Admins, in either group,
 * most recently active first, second page. Each facet is a parameter named for its column, repeated
 * once per chosen value; `sort` is a column, `-` first when descending; `page` counts from 1; and
 * `q`, in a table that can be searched, is the text its rows are searched for.
 *
 * A table that shares its page with others names each of its parameters under a prefix of its own —
 * `runs-sort=-spent&runs-page=2` — and leaves every parameter that is not its own alone.
 */

/** One column, and which way. The direction's words are `aria-sort`'s. */
export type Sort<Column extends string> = {
  column: Column
  direction: "ascending" | "descending"
}

/**
 * Every facet, chosen values or none; always a sort; always a page, from 1; and in a table that can
 * be searched, the search, "" for none.
 */
export type TableView<Facet extends string, Column extends string> = {
  filters: Record<Facet, readonly string[]>
  sort: Sort<Column>
  page: number
  q?: string
}

/**
 * What a table accepts: the columns that can be filtered and sorted, and the sort it starts with,
 * the prefix its parameters are named under, if it has one, and whether its rows can be searched.
 * Facets must not be called `sort`, `page` or `q`.
 */
export type TableShape<Facet extends string, Column extends string> = {
  prefix?: string
  facets: readonly Facet[]
  sortable: readonly Column[]
  sort: Sort<Column>
  searchable?: boolean
}

/**
 * The view a URL shows. Reading never fails: whatever is absent or cannot be read — an unknown
 * column, a page that is not a whole number from 1 — is read as the default, a facet's empty and
 * repeated values are dropped, and parameters that are not the table's are ignored. A facet value
 * no row has is kept, and matches nothing. A search is read as it was typed, spaces and all, so
 * the field showing it can be typed into; a table that can't be searched reads none.
 */
export function readTableView<Facet extends string, Column extends string>(
  params: URLSearchParams,
  shape: TableShape<Facet, Column>,
): TableView<Facet, Column> {
  const name = named(shape)
  return {
    filters: byFacet(shape.facets, (facet) =>
      [...new Set(params.getAll(name(facet)))].filter(Boolean),
    ),
    sort: readSort(params.get(name("sort")), shape),
    page: readPage(params.get(name("page"))),
    ...(shape.searchable && { q: params.get(name("q")) ?? "" }),
  }
}

/** A parameter's name in the URL: as it is, or under the table's prefix. */
function named(shape: TableShape<string, string>) {
  return (parameter: string) => (shape.prefix ? `${shape.prefix}-${parameter}` : parameter)
}

function byFacet<Facet extends string>(
  facets: readonly Facet[],
  valuesOf: (facet: Facet) => readonly string[],
) {
  return facets.reduce(
    (filters, facet) => ({ ...filters, [facet]: valuesOf(facet) }),
    {} as Record<Facet, readonly string[]>,
  )
}

function readSort<Column extends string>(
  param: string | null,
  shape: TableShape<string, Column>,
): Sort<Column> {
  if (!param) return shape.sort
  const descending = param.startsWith("-")
  const column = shape.sortable.find((sortable) => sortable === param.slice(descending ? 1 : 0))
  return column ? { column, direction: descending ? "descending" : "ascending" } : shape.sort
}

function readPage(param: string | null) {
  const page = Number(param)
  return Number.isInteger(page) && page >= 1 ? page : 1
}

/**
 * The search parameters that show a view, holding only its departures from the default, so the
 * default view is the page's own bare URL. Reading them back gives the same view. `page` is the
 * rest of the page's parameters, kept as they are but for the table's own.
 */
export function writeTableView<Facet extends string, Column extends string>(
  view: TableView<Facet, Column>,
  shape: TableShape<Facet, Column>,
  page = new URLSearchParams(),
) {
  const name = named(shape)
  const own = new Set(
    [...shape.facets, "sort", "page", ...(shape.searchable ? ["q"] : [])].map(name),
  )
  const params = new URLSearchParams([...page].filter(([key]) => !own.has(key)))
  for (const facet of shape.facets)
    for (const value of view.filters[facet]) params.append(name(facet), value)
  const { column, direction } = view.sort
  if (column !== shape.sort.column || direction !== shape.sort.direction)
    params.set(name("sort"), direction === "descending" ? `-${column}` : column)
  if (view.page > 1) params.set(name("page"), String(view.page))
  if (shape.searchable && view.q) params.set(name("q"), view.q)
  return params
}
